import { getToken, onMessage, type MessagePayload } from 'firebase/messaging';
import { messaging } from '../config/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface NotificationPayload {
  title: string;
  body: string;
  groupId?: string;
  groupName?: string;
  senderName?: string;
  messageId?: string;
}

export interface InBrowserNotification {
  id: string;
  title: string;
  body: string;
  groupId?: string;
  groupName?: string;
  senderName?: string;
  timestamp: Date;
  avatar?: string;
}

class NotificationService {
  private static instance: NotificationService;
  private vapidKey = 'BMl6znGaKQGQMFh016cAFkrTA8cKWlZkqeca-RjhBcaNbOtjnoLAXdvCSuq4uNK2kVDLFFgVGzMfvGIAO0kF59s';
  private isInitialized = false;
  private currentGroupId: string | null = null;
  private isPageVisible = true;
  private inBrowserNotifications: InBrowserNotification[] = [];
  private notificationListeners: ((notifications: InBrowserNotification[]) => void)[] = [];
  private userNotificationPreferences: { inBrowserEnabled: boolean; pushEnabled: boolean } = { inBrowserEnabled: true, pushEnabled: true };

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  private constructor() {
    this.initializeMessaging();
    this.initializePageVisibilityTracking();
  }

  private async initializeMessaging() {
    if (!messaging) {
      console.warn('Firebase Messaging not supported in this browser');
      return;
    }

    // Handle foreground messages
    onMessage(messaging, (payload: MessagePayload) => {
      this.showForegroundNotification(payload);
    });

    this.isInitialized = true;
  }

  private initializePageVisibilityTracking() {
    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      this.isPageVisible = !document.hidden;
    });

    // Track window focus/blur
    window.addEventListener('focus', () => {
      this.isPageVisible = true;
    });

    window.addEventListener('blur', () => {
      this.isPageVisible = false;
    });

    // Track page navigation to clear group context when leaving community page
    let lastPath = window.location.pathname;
    const checkPathChange = () => {
      const currentPath = window.location.pathname;
      if (currentPath !== lastPath) {
        // If navigating away from community page, clear group context
        if (lastPath === '/community' && currentPath !== '/community') {
          this.currentGroupId = null;
        }
        
        lastPath = currentPath;
      }
    };

    // Use MutationObserver to detect navigation in SPAs
    const observer = new MutationObserver(checkPathChange);
    observer.observe(document.querySelector('title') || document.head, {
      childList: true,
      subtree: true
    });

    // Also check on popstate (back/forward buttons)
    window.addEventListener('popstate', checkPathChange);
  }

  public async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    try {
      const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // Check current permission status first
      if (Notification.permission === 'granted') {
        return true;
      }

      if (Notification.permission === 'denied') {
        return false;
      }

      // For Safari and older browsers that don't support the Promise-based API
      let permission: NotificationPermission;
      
      if (typeof Notification.requestPermission === 'function') {
        if (Notification.requestPermission.length === 0) {
          // Modern Promise-based API (Chrome, Firefox)
          permission = await Notification.requestPermission();
        } else {
          // Legacy callback-based API (Safari)
          permission = await new Promise((resolve) => {
            Notification.requestPermission((result) => resolve(result));
          });
        }
      } else {
        permission = Notification.permission;
      }
      
      if (permission === 'granted') {
        // Test if notifications actually work with different options for different browsers
        try {
          let testOptions: NotificationOptions = {
            body: 'Notifications are now enabled!',
            icon: '/images/Logos/T1 Logo Black200.png',
            tag: 'permission-test'
          };

          // Chrome-specific optimizations
          if (isChrome) {
            testOptions = {
              ...testOptions,
              silent: false,
              requireInteraction: false
            };
          }

          // Mobile-specific optimizations  
          if (isMobile) {
            testOptions = {
              body: 'Notifications enabled!',
              tag: 'permission-test'
              // Remove icon and other options that might cause issues on mobile
            };
          }

          const testNotification = new Notification('T1 FX Team', testOptions);
          
          // Auto-close test notification
          setTimeout(() => {
            try {
              testNotification.close();
            } catch (e) {
              // Ignore close errors
            }
          }, 3000);
          
        } catch (testError) {
          console.warn('🔔 Test notification failed:', testError);
          // Don't fail the permission request if test notification fails
        }
      }

      return permission === 'granted';
    } catch (error) {
      console.error('🔔 Error requesting notification permission:', error);
      return false;
    }
  }

  public async getNotificationToken(userId: string): Promise<string | null> {
    if (!messaging || !this.isInitialized) {
      console.warn('Messaging not initialized');
      return null;
    }

    try {
      // Check if service worker is registered first
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const swRegistration = registrations.find(reg => 
          reg.scope.includes('firebase-messaging-sw') || 
          reg.active?.scriptURL.includes('firebase-messaging-sw')
        );
        
        if (!swRegistration) {
          console.log('Registering service worker for FCM...');
          await navigator.serviceWorker.register('/firebase-messaging-sw.js');
          // Wait a bit for registration to complete
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      // Add a timeout to prevent hanging
      const tokenPromise = getToken(messaging, { vapidKey: this.vapidKey });
      const timeoutPromise = new Promise<null>((_, reject) => {
        setTimeout(() => reject(new Error('FCM token request timeout')), 15000);
      });
      
      const currentToken = await Promise.race([tokenPromise, timeoutPromise]);
      
      if (currentToken && typeof currentToken === 'string') {
        console.log('FCM Token obtained:', currentToken.substring(0, 20) + '...');
        
        // Save token to Firestore for the user with retry logic
        await this.saveTokenToFirestore(userId, currentToken);
        
        // Verify token was saved successfully
        const verification = await this.verifyTokenSaved(userId, currentToken);
        if (!verification) {
          console.warn('Token may not have been saved properly, retrying...');
          await this.saveTokenToFirestore(userId, currentToken);
        }
        
        return currentToken;
      } else {
        console.log('No registration token available.');
        return null;
      }
    } catch (error) {
      console.error('Error getting FCM token:', error);
      
      // Try to re-register service worker if there's an issue
      if ('serviceWorker' in navigator) {
        try {
          // Unregister any existing service workers first
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const registration of registrations) {
            if (registration.scope.includes('firebase-messaging-sw') || 
                registration.active?.scriptURL.includes('firebase-messaging-sw')) {
              await registration.unregister();
              console.log('Unregistered existing FCM service worker');
            }
          }
          
          // Register fresh service worker
          const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
          console.log('Service worker re-registered:', registration);
          
          // Wait for service worker to be ready
          await navigator.serviceWorker.ready;
          
          // Retry token retrieval
          if (messaging) {
            const retryToken = await getToken(messaging, { vapidKey: this.vapidKey });
            if (retryToken) {
              await this.saveTokenToFirestore(userId, retryToken);
              return retryToken;
            }
          }
        } catch (swError) {
          console.error('Service worker re-registration failed:', swError);
        }
      }
      
      // If all else fails, throw the original error for upstream handling
      throw error;
    }
  }

  private async saveTokenToFirestore(userId: string, token: string): Promise<void> {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const currentTokens = userDoc.data().fcmTokens || [];
        
        // Add token if it doesn't exist
        if (!currentTokens.includes(token)) {
          await setDoc(userDocRef, {
            fcmTokens: [...currentTokens, token],
            lastTokenUpdate: new Date()
          }, { merge: true });
          console.log('FCM token saved to Firestore successfully');
        } else {
          console.log('FCM token already exists in Firestore');
        }
      } else {
        // Create user document if it doesn't exist
        await setDoc(userDocRef, {
          fcmTokens: [token],
          lastTokenUpdate: new Date(),
          notificationsEnabled: true
        }, { merge: true });
        console.log('Created user document with FCM token');
      }
    } catch (error) {
      console.error('Error saving FCM token to Firestore:', error);
      throw error; // Re-throw for upstream handling
    }
  }

  private async verifyTokenSaved(userId: string, token: string): Promise<boolean> {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const fcmTokens = userData.fcmTokens || [];
        return fcmTokens.includes(token);
      }
      return false;
    } catch (error) {
      console.error('Error verifying token save:', error);
      return false;
    }
  }

  private showForegroundNotification(payload: MessagePayload): void {
    const { notification, data } = payload;
    
    console.log('🔔 Processing foreground notification...', { notification, data });
    
    if (!notification) {
      console.log('🔔 No notification data in payload, skipping');
      return;
    }

    // Extract group ID from notification data
    const messageGroupId = data?.groupId;
    
    // Check if user is on the Community page
    const isOnCommunityPage = window.location.pathname === '/community';
    
    // Check if user is actively viewing this specific chat
    const isViewingThisSpecificChat = this.isPageVisible && 
                                      isOnCommunityPage &&
                                      this.currentGroupId && 
                                      this.currentGroupId === messageGroupId;
    
    console.log('🔔 Notification context decision:', {
      isPageVisible: this.isPageVisible,
      isOnCommunityPage,
      currentGroupId: this.currentGroupId,
      messageGroupId,
      isViewingThisSpecificChat,
      currentPath: window.location.pathname
    });

    // Show notification if:
    // 1. Page/tab is not visible/focused, OR
    // 2. User is on a different page (not /community), OR  
    // 3. User is on community page but viewing a different chat, OR
    // 4. User is on community page but no chat is selected
    const shouldShowNotification = !this.isPageVisible || 
                                   !isOnCommunityPage || 
                                   !isViewingThisSpecificChat;

    console.log('🔔 Notification decision:', {
      shouldShowNotification,
      reasons: {
        pageNotVisible: !this.isPageVisible,
        notOnCommunityPage: !isOnCommunityPage,
        notViewingThisChat: !isViewingThisSpecificChat
      }
    });

    if (shouldShowNotification) {
      // Show in-browser card notification if enabled
      if (this.userNotificationPreferences.inBrowserEnabled) {
        this.createInBrowserNotification(notification, data);
      }

      // Show system notification if enabled
      if (this.userNotificationPreferences.pushEnabled) {
        try {
          // Check permission before creating system notification
          if (Notification.permission !== 'granted') {
            console.warn('🔔 ❌ Cannot show system notification - permission not granted:', Notification.permission);
            return;
          }

        // Detect browser and mobile for optimal notification options
        const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        let notificationOptions: NotificationOptions;

        if (isMobile) {
          // Simplified options for mobile browsers
          notificationOptions = {
            body: notification.body || 'New message',
            tag: 'chat-notification'
          };
        } else if (isChrome) {
          // Chrome-optimized options
          notificationOptions = {
            body: notification.body || 'New message received',
            icon: '/images/Logos/T1 Logo Black200.png',
            tag: 'chat-notification',
            data: data,
            requireInteraction: false,
            silent: false
          };
        } else {
          // Safari and other browsers
          notificationOptions = {
            body: notification.body || 'New message received',
            icon: '/images/Logos/T1 Logo Black200.png',
            tag: 'chat-notification',
            data: data
          };
        }

        console.log('🔔 Creating notification with options:', notificationOptions);

        const browserNotification = new Notification(notification.title || 'New Message', notificationOptions);
        
        console.log('🔔 ✅ Successfully created foreground notification for group:', messageGroupId);
        
        // Add event handlers for debugging
        browserNotification.onshow = () => {
          console.log('🔔 ✅ Notification displayed successfully');
        };
        
        browserNotification.onerror = (error: Event) => {
          console.error('🔔 ❌ Notification error:', error);
        };
        
        browserNotification.onclick = () => {
          console.log('🔔 Notification clicked - focusing window');
          window.focus();
          browserNotification.close();
          
          // Navigate to community page if not there
          if (window.location.pathname !== '/community') {
            window.location.href = '/community';
          }
        };
        
        browserNotification.onclose = () => {
          console.log('🔔 Notification closed');
        };
        
        // Auto-close after 10 seconds to prevent notification buildup
        setTimeout(() => {
          try {
            browserNotification.close();
          } catch (e) {
            // Notification might already be closed
          }
        }, 10000);
        
      } catch (error) {
        console.error('🔔 ❌ Error creating notification:', error);
        console.error('🔔 Error details:', {
          name: error instanceof Error ? error.name : 'Unknown',
          message: error instanceof Error ? error.message : String(error),
          permission: Notification.permission,
          userAgent: navigator.userAgent
        });
        
        // Fallback: try a simpler notification
        try {
          const simpleNotification = new Notification(notification.title || 'New Message');
          console.log('🔔 ✅ Fallback simple notification created');
          setTimeout(() => simpleNotification.close(), 5000);
        } catch (fallbackError) {
          console.error('🔔 ❌ Even simple notification failed:', fallbackError);
        }
      }
      }
    } else {
      console.log('🔔 ❌ Suppressed notification - user is actively viewing this chat:', messageGroupId);
    }
  }

  private createInBrowserNotification(notification: any, data: any): void {
    const inBrowserNotification: InBrowserNotification = {
      id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title: notification.title || 'New Message',
      body: notification.body || 'You have a new message',
      groupId: data?.groupId,
      groupName: data?.groupName,
      senderName: data?.senderName,
      timestamp: new Date(),
      avatar: data?.avatar
    };

    // Add to notifications array
    this.inBrowserNotifications.unshift(inBrowserNotification);

    // Keep only the latest 10 notifications
    if (this.inBrowserNotifications.length > 10) {
      this.inBrowserNotifications = this.inBrowserNotifications.slice(0, 10);
    }

    // Notify all listeners
    this.notifyListeners();

    // Auto-remove after 10 seconds
    setTimeout(() => {
      this.removeInBrowserNotification(inBrowserNotification.id);
    }, 10000);
  }

  private notifyListeners(): void {
    this.notificationListeners.forEach(listener => {
      listener([...this.inBrowserNotifications]);
    });
  }

  public addNotificationListener(listener: (notifications: InBrowserNotification[]) => void): () => void {
    this.notificationListeners.push(listener);
    // Return unsubscribe function
    return () => {
      const index = this.notificationListeners.indexOf(listener);
      if (index > -1) {
        this.notificationListeners.splice(index, 1);
      }
    };
  }

  public removeInBrowserNotification(id: string): void {
    this.inBrowserNotifications = this.inBrowserNotifications.filter(n => n.id !== id);
    this.notifyListeners();
  }

  public getInBrowserNotifications(): InBrowserNotification[] {
    return [...this.inBrowserNotifications];
  }

  public clearAllInBrowserNotifications(): void {
    this.inBrowserNotifications = [];
    this.notifyListeners();
  }

  public setUserNotificationPreferences(preferences: { inBrowserEnabled: boolean; pushEnabled: boolean }): void {
    this.userNotificationPreferences = preferences;
  }

  public getInBrowserNotificationPreferences(): { inBrowserEnabled: boolean; pushEnabled: boolean } {
    return { ...this.userNotificationPreferences };
  }

  private showInAppNotification(title: string, body: string): void {
    // Create a simple toast notification for in-app use
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 80px;
      right: 20px;
      background: #424242;
      color: white;
      padding: 16px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 10000;
      max-width: 300px;
      animation: slideIn 0.3s ease-out;
    `;
    
    notification.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 4px;">${title}</div>
      <div style="font-size: 14px; opacity: 0.9;">${body}</div>
    `;

    // Add animation styles
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(notification);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      notification.style.animation = 'slideIn 0.3s ease-out reverse';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 5000);

    // Add click to dismiss
    notification.addEventListener('click', () => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    });
  }

  public async sendNotificationToGroup(
    groupId: string,
    payload: NotificationPayload,
    excludeUserId?: string
  ): Promise<void> {
    // This would typically be done on the backend
    // Here we're showing how it could be implemented
    console.log('Send notification to group:', groupId, payload, 'excluding:', excludeUserId);
    
    // In a real implementation, this would call a Cloud Function
    // that handles sending notifications to all group members
  }

  public async updateNotificationPreferences(userId: string, enabled: boolean): Promise<void> {
    try {
      const userDocRef = doc(db, 'users', userId);
      await setDoc(userDocRef, {
        notificationsEnabled: enabled,
        lastNotificationUpdate: new Date()
      }, { merge: true });

      if (enabled && this.getPermissionStatus() === 'default') {
        await this.requestPermission();
      }
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  }

  public async getUserNotificationPreferences(userId: string): Promise<boolean> {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        return userDoc.data().notificationsEnabled !== false; // Default to true
      }
      return true;
    } catch (error) {
      console.error('Error getting notification preferences:', error);
      return true;
    }
  }

  public async initializeForUser(userId: string): Promise<void> {
    if (!this.isNotificationSupported()) {
      console.warn('Notifications not supported');
      return;
    }

    const userPrefs = await this.getUserNotificationPreferences(userId);
    
    if (userPrefs) {
      const hasPermission = await this.requestPermission();
      if (hasPermission) {
        // Clean up old tokens first
        await this.cleanupOldTokens(userId);
        
        // Get new token
        const token = await this.getNotificationToken(userId);
        
        if (token) {
          console.log('Notifications initialized successfully for user:', userId);
        } else {
          console.warn('Failed to obtain FCM token for user:', userId);
        }
      } else {
        console.log('Notification permission not granted');
      }
    } else {
      console.log('Notifications disabled by user preference');
    }
  }

  private async cleanupOldTokens(userId: string): Promise<void> {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const fcmTokens = userData.fcmTokens || [];
        
        // If there are more than 3 tokens, keep only the 2 most recent ones
        // This prevents accumulation of old tokens from multiple devices/browsers
        if (fcmTokens.length > 3) {
          const recentTokens = fcmTokens.slice(-2); // Keep last 2 tokens
          
          await setDoc(userDocRef, {
            fcmTokens: recentTokens,
            lastTokenCleanup: new Date()
          }, { merge: true });
          
          console.log(`Cleaned up old FCM tokens for user ${userId}: ${fcmTokens.length} -> ${recentTokens.length}`);
        }
      }
    } catch (error) {
      console.error('Error cleaning up old tokens:', error);
      // Don't throw - this is a maintenance operation
    }
  }

  public getPermissionStatus(): NotificationPermission {
    return Notification.permission;
  }

  public isNotificationSupported(): boolean {
    const hasNotifications = 'Notification' in window;
    const hasServiceWorker = 'serviceWorker' in navigator;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    console.log('🔔 Browser support check:', {
      hasNotifications,
      hasServiceWorker,
      isSafari,
      userAgent: navigator.userAgent,
      platform: navigator.platform
    });
    
    return hasNotifications && hasServiceWorker;
  }

  public setCurrentGroup(groupId: string | null): void {
    this.currentGroupId = groupId;
    console.log('Set current group for notifications:', groupId);
  }

  public getCurrentGroup(): string | null {
    return this.currentGroupId;
  }

  public getPageVisibility(): boolean {
    return this.isPageVisible;
  }

  public clearGroupContext(): void {
    this.currentGroupId = null;
    console.log('Cleared group context for notifications');
  }

  public getCurrentNotificationContext(): Promise<object> {
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isHTTPS = window.location.protocol === 'https:';
    
    return Promise.resolve({
      currentGroupId: this.currentGroupId,
      isPageVisible: this.isPageVisible,
      currentPath: window.location.pathname,
      isOnCommunityPage: window.location.pathname === '/community',
      permissionStatus: this.getPermissionStatus(),
      isNotificationSupported: this.isNotificationSupported(),
      browserInfo: {
        isChrome,
        isMobile,
        isAndroid,
        isHTTPS,
        userAgent: navigator.userAgent,
        vendor: navigator.vendor || 'unknown'
      },
      troubleshooting: {
        chromeRequiresHTTPS: isChrome && !isHTTPS,
        mobileRequiresUserGesture: isMobile,
        androidChromeIssues: isAndroid && isChrome
      },
      timestamp: new Date().toISOString()
    });
  }
}

export default NotificationService.getInstance(); 